import { Suspense, lazy, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, ChevronLeft, ChevronRight, Film, Globe, Image as ImageIcon, Layers, Loader2, Lock, Star } from 'lucide-react';

// Lazy-loaded so the ~600 KB model-viewer bundle only enters the AvatarDetail
// chunk when a 3D-ready avatar actually renders.
const ModelViewer = lazy(() => import('@/components/hub/ModelViewer'));

/**
 * Phase 7c — the server prepends a long magenta-background instruction to the
 * user prompt when transparency !== 'none', and persists that wrapped string
 * on the clip. For re-roll we want to ship the user's original phrasing back
 * to the server (which will re-wrap if transparency is still set), so strip
 * the known prefix here. Matches the verbatim string in video2dService.ts.
 */
const CHROMA_KEY_PROMPT_PREFIX =
  `On a perfectly uniform, pure solid magenta background (hex #FF00FF, 100% saturation, no gradients, no shadows on the background). ` +
  `Critical: the subject and its clothing/props must NOT contain any magenta, pink, hot pink, or pinkish-purple anywhere — replace any such colors with neutral tones. ` +
  `Action: `;
function stripChromaKeyPrefix(prompt: string): string {
  return prompt.startsWith(CHROMA_KEY_PROMPT_PREFIX)
    ? prompt.slice(CHROMA_KEY_PROMPT_PREFIX.length)
    : prompt;
}

/**
 * Veo clips never get the magenta preprocessing — the backend silently
 * auto-upgrades chroma-key → matting on Veo, so the persisted chromaKeyHex
 * is stale. The actual RGB stream from veed has the natural cinematic
 * backdrop already masked toward black, so decomposite with bg=#FF00FF
 * would amplify any green/scene residue into a visible halo. Force black
 * decomposite for Veo clips regardless of chromaKeyHex.
 */
function isVeoProvider(p: string | undefined): boolean {
  return p === 'veo31-fal' || p === 'veo31-fast-fal';
}
function effectiveBgHexFor(clip: { provider?: string; chromaKeyHex?: string }): string {
  if (isVeoProvider(clip.provider)) return '#000000';
  return clip.chromaKeyHex ?? '#000000';
}

import {
  useAnimate2dMutation,
  useGetAvatarQuery,
  useGetAvatarSeriesQuery,
  useGetLibrariesQuery,
  useGetTemplatesQuery,
  useReskinMutation,
  useSetActiveSkinMutation,
  useSetFeaturedAvatarMutation,
  useUpdateAvatarMutation,
  type AnimationLibrary,
  type AvatarItem,
  type Video2dProviderId,
  type VideoClipTransparency,
} from '@/services/avatarApi';
import { Loader } from '@/components/common/Loader';
import { Make3dCta } from '@/components/avatars/Make3dCta';
import { AnimateMeCta } from '@/components/avatars/AnimateMeCta';
import { AnimateLibraryModal } from '@/components/avatars/AnimateLibraryModal';
import { ReskinModal } from '@/components/avatars/ReskinModal';
import { SkinSelector } from '@/components/avatars/SkinSelector';
import { Animate2dModal } from '@/components/avatars/Animate2dModal';
import { VideoClipsList } from '@/components/avatars/VideoClipsList';
import { VideoClipDetailsModal } from '@/components/avatars/VideoClipDetailsModal';
import { ChromaKeyVideo } from '@/components/avatars/ChromaKeyVideo';
import { DualStreamVideo } from '@/components/avatars/DualStreamVideo';
import { supportsVp9Alpha } from '@/lib/videoAlphaSupport';
import { AnimationOverlay } from '@/components/avatars/AnimationOverlay';
import { AnimationPicker } from '@/components/avatars/AnimationPicker';
import { PackSwitcher } from '@/components/avatars/PackSwitcher';
import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';
import { usePuoCreareAvatar } from '@/hooks/usePuoCreareAvatar';
import { useMeshLifecycle } from '@/hooks/useMeshLifecycle';
import { useAnimationLifecycle } from '@/hooks/useAnimationLifecycle';
import { cn } from '@/lib/cn';

export function AvatarDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation('avatars');

  const haptics = useHaptics();
  // I-416: con il flag Studio acceso (solo staging) tutto cio' che CREA o SPENDE (modifica con l'AI, 3D, animazioni 3D,
  // reskin, clip video) e' solo di `administrator`, come le nove rotte di Qavatar#10. Un utente normale vede e usa
  // l'avatar, non ne genera altri. Flag spento: come oggi (I-270).
  const puoCreare = usePuoCreareAvatar();
  // Phase 7a — when an in-flight video2d clip OR in-flight skin exists, poll
  // the avatar every 5s so the carousel auto-refreshes as the worker progresses.
  // RTK Query treats pollingInterval=0 as "no polling".
  const [avatarPollMs, setAvatarPollMs] = useState(0);
  const { data: avatar, isLoading } = useGetAvatarQuery(id, {
    skip: !id,
    pollingInterval: avatarPollMs,
  });
  useEffect(() => {
    const inFlightClip = (avatar?.videoClips ?? []).some(c => c.status === 'queued' || c.status === 'generating');
    const inFlightSkin = (avatar?.mesh?.skins ?? []).some(s => s.status === 'queued' || s.status === 'retexturing');
    setAvatarPollMs(inFlightClip || inFlightSkin ? 5000 : 0);
  }, [avatar?.videoClips, avatar?.mesh?.skins]);
  const [setFeatured, { isLoading: settingFeatured }] = useSetFeaturedAvatarMutation();
  const [updateAvatar, { isLoading: updatingVisibility }] = useUpdateAvatarMutation();
  const { mesh, trigger: triggerMesh, isPending: isMeshPending } = useMeshLifecycle(
    id,
    avatar?.mesh,
  );

  const animLife = useAnimationLifecycle(id);
  const [showAnimOverlay, setShowAnimOverlay] = useState(false);
  const [libraryModalOpen, setLibraryModalOpen] = useState(false);
  const [reskinModalOpen, setReskinModalOpen] = useState(false);
  const [reskin, { isLoading: isReskinPending }] = useReskinMutation();
  const [setActiveSkin] = useSetActiveSkinMutation();

  const onPickSkin = useCallback(async (skinId: string | null) => {
    if (!id) return;
    try {
      await setActiveSkin({ avatarId: id, skinId }).unwrap();
    } catch (err) {
      console.error('[AvatarDetail] setActiveSkin failed', err);
    }
  }, [id, setActiveSkin]);

  const onSubmitReskin = useCallback(async (textPrompt: string, referenceImage?: File) => {
    if (!id) return;
    try {
      await reskin({
        avatarId: id,
        ...(textPrompt ? { textPrompt } : {}),
        ...(referenceImage ? { referenceImage } : {}),
      }).unwrap();
      setReskinModalOpen(false);
    } catch (err) {
      console.error('[AvatarDetail] reskin enqueue failed', err);
    }
  }, [id, reskin]);

  // Phase 7a — 2D animation (image-to-video)
  const [animate2dOpen, setAnimate2dOpen] = useState(false);
  const [detailsClipId, setDetailsClipId] = useState<string | null>(null);
  const [animate2d, { isLoading: isAnimate2dPending }] = useAnimate2dMutation();
  // setActiveVideoClip is now owned by VideoClipsList / AvatarClipDetail —
  // the old onPickVideoClip helper here was deleted along with the
  // legacy Static/picker UI.

  const [animate2dError, setAnimate2dError] = useState<string | null>(null);
  const onSubmitAnimate2d = useCallback(async (params: {
    prompt: string;
    provider: Video2dProviderId;
    durationSec: 5 | 6 | 10;
    resolution: '512p' | '768p' | '1080p';
    variants?: 1 | 2 | 3;
    transparency?: VideoClipTransparency;
    seed?: number;
  }) => {
    if (!id) return;
    setAnimate2dError(null);
    try {
      await animate2d({ avatarId: id, ...params }).unwrap();
      setAnimate2dOpen(false);
    } catch (err: unknown) {
      console.error('[AvatarDetail] animate2d enqueue failed', err);
      // Surface the server's machine-readable error to the modal so the
      // user gets feedback instead of a silent no-op. Include HTTP status
      // when no business-reason string is available — that way an unknown
      // 401/403/500 doesn't collapse to a useless "Couldn't submit".
      const e = err as {
        status?: number;
        originalStatus?: number;
        data?: { error?: string; message?: string } | string;
        error?: string;
        message?: string;
      };
      const status = e.status ?? e.originalStatus;
      const dataObj = typeof e.data === 'object' ? e.data : undefined;
      const serverMsg =
        dataObj?.error ??
        dataObj?.message ??
        (typeof e.data === 'string' ? e.data : undefined);
      const fallback = e.error ?? e.message ?? 'submission_failed';
      const detail = status != null ? ` (HTTP ${status})` : '';
      setAnimate2dError(`${serverMsg ?? fallback}${detail}`);
    }
  }, [id, animate2d]);

  // Phase 7b — re-roll an existing video clip with the same prompt/provider.
  // Phase 7c — also re-uses the original clip's transparency mode so the
  // re-roll matches what the user expects (no silent switch back to opaque).
  // The persisted clip.prompt for chroma-key clips contains the magenta
  // wrapper from the server; re-submitting it doesn't double-wrap because
  // the server only prefixes when transparency !== 'none', and on re-roll
  // we send the already-wrapped prompt — which would prepend the wrapper
  // again. To avoid that, strip the known wrapper prefix here before sending.
  const onRerollVideoClip = useCallback(async (cid: string) => {
    if (!id || !avatar) return;
    const clip = (avatar.videoClips ?? []).find(c => c.clipId === cid);
    if (!clip) return;
    const cleanPrompt = stripChromaKeyPrefix(clip.prompt);
    try {
      await animate2d({
        avatarId: id,
        prompt: cleanPrompt,
        provider: clip.provider,
        durationSec: (clip.durationSec ?? 6) as 5 | 6 | 10,
        resolution: clip.resolution,
        label: `${clip.label ?? cleanPrompt} (reroll)`,
        transparency: clip.transparency,
        // Re-roll deliberately omits the seed so we get a different output
        // — that's the point of re-rolling. Retry (below) DOES carry the
        // seed so the user can replay an exact failed run.
      }).unwrap();
    } catch (err) {
      console.error('[AvatarDetail] reroll failed', err);
    }
  }, [id, avatar, animate2d]);

  /** Phase 7e — replay the same configuration (incl. seed) on a failed
   *  clip. Identical to the original submission. */
  const onRetryVideoClip = useCallback(async (cid: string) => {
    if (!id || !avatar) return;
    const clip = (avatar.videoClips ?? []).find(c => c.clipId === cid);
    if (!clip) return;
    const cleanPrompt = stripChromaKeyPrefix(clip.prompt);
    try {
      await animate2d({
        avatarId: id,
        prompt: cleanPrompt,
        provider: clip.provider,
        durationSec: (clip.durationSec ?? 6) as 5 | 6 | 10,
        resolution: clip.resolution,
        label: `${clip.label ?? cleanPrompt} (retry)`,
        transparency: clip.transparency,
        seed: clip.seed,
      }).unwrap();
    } catch (err) {
      console.error('[AvatarDetail] retry failed', err);
    }
  }, [id, avatar, animate2d]);

  // Phase 3 closure — resolve the avatar's template to drive the animation
  // library filter. Cached at the RTK Query level; the templates list is small.
  const { data: templates } = useGetTemplatesQuery();
  const templateCompatibleProviders = useMemo(() => {
    if (!avatar?.templateId || !templates) return undefined;
    const tpl = templates.find(tp => tp._id === avatar.templateId);
    return tpl?.compatibleProviders;
  }, [avatar?.templateId, templates]);

  // Series sibling navigation — fetch the full series so users can step prev/
  // next between variations of this avatar without leaving the detail page.
  // Skip the query for single-version avatars (saves a network round-trip).
  const seriesCount = avatar?.seriesCount ?? 1;
  const { data: seriesItems } = useGetAvatarSeriesQuery(id, {
    skip: !id || seriesCount <= 1,
  });
  const { prevAvatar, nextAvatar, currentIndex, total } = useMemo<{
    prevAvatar: AvatarItem | null;
    nextAvatar: AvatarItem | null;
    currentIndex: number;
    total: number;
  }>(() => {
    const list = seriesItems ?? [];
    if (list.length <= 1) {
      return { prevAvatar: null, nextAvatar: null, currentIndex: -1, total: list.length };
    }
    const idx = list.findIndex((a) => a._id === id);
    if (idx < 0) {
      return { prevAvatar: null, nextAvatar: null, currentIndex: -1, total: list.length };
    }
    // Backend returns series sorted DESC by createdAt (newest first). For UX
    // we expose `prev` = newer (index-1, stop at 0) and `next` = older
    // (index+1, stop at last). No wrap-around.
    return {
      prevAvatar: idx > 0 ? list[idx - 1] : null,
      nextAvatar: idx < list.length - 1 ? list[idx + 1] : null,
      currentIndex: idx,
      total: list.length,
    };
  }, [seriesItems, id]);

  const goSibling = useCallback(
    (target: AvatarItem | null) => {
      if (!target) return;
      haptics.selection();
      navigate(`/avatars/${target._id}`, { replace: true });
    },
    [navigate, haptics],
  );

  // Phase 3 — active pack + preset labels from /libraries catalog.
  // Used by AnimationPicker to render user-facing chip labels and as the GLB
  // source for the viewer when an animated pack is active.
  const activePack = useMemo(() => {
    if (!mesh?.animationPacks || !mesh.activePackId) return undefined;
    return mesh.animationPacks.find(p => p.packId === mesh.activePackId);
  }, [mesh?.animationPacks, mesh?.activePackId]);

  // Catalog labels for chip rendering. Library-specific mapping is server-side
  // in our catalog files; here we fetch the metadata and use the in-pack clipName
  // when present, falling back to a humanized id.
  useGetLibrariesQuery();  // warm cache so the modal/chip labels load fast

  const onOpenLibraryModal = useCallback(() => {
    haptics.impact('light');
    setLibraryModalOpen(true);
  }, [haptics]);

  const onPickLibrary = useCallback(
    async (library: AnimationLibrary, force: boolean, presetIds?: string[]) => {
      setLibraryModalOpen(false);
      try {
        await animLife.trigger(library, force, presetIds);
        setShowAnimOverlay(true);
      } catch (err) {
        console.error('[AvatarDetail] animate3d failed', err);
        haptics.notification('error');
      }
    },
    [animLife, haptics],
  );

  const onMake3d = useCallback(
    async (force: boolean, provider?: 'tripo' | 'meshy' | 'rodin-fal') => {
      haptics.impact('light');
      try {
        await triggerMesh(force, provider);
      } catch (err) {
        console.error('[AvatarDetail] upgrade3d failed', err);
        haptics.notification('error');
      }
    },
    [haptics, triggerMesh],
  );

  const onEdit = useCallback(() => navigate(`/avatars/${id}/edit`), [navigate, id]);

  const onSetFeatured = useCallback(async () => {
    if (!avatar || avatar.isFeatured) return;
    haptics.impact('light');
    try {
      await setFeatured(avatar._id).unwrap();
      haptics.notification('success');
    } catch (err) {
      console.error('[AvatarDetail] set featured failed', err);
      haptics.notification('error');
    }
  }, [avatar, setFeatured, haptics]);

  const onToggleVisibility = useCallback(async () => {
    if (!avatar) return;
    const next = avatar.visibility === 'public' ? 'private' : 'public';
    haptics.impact('light');
    try {
      await updateAvatar({ id: avatar._id, visibility: next }).unwrap();
      haptics.notification('success');
    } catch (err) {
      console.error('[AvatarDetail] toggle visibility failed', err);
      haptics.notification('error');
    }
  }, [avatar, updateAvatar, haptics]);

  useMainButton(
    puoCreare
      ? {
          text: t('detail.editWithAi'),
          enabled: !!avatar,
          onClick: onEdit,
        }
      : null,
  );

  if (isLoading || !avatar) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader />
      </div>
    );
  }

  const aspectClass = avatar.aspectRatio === '3:4' ? 'aspect-[3/4]' : 'aspect-square';

  // Pick GLB + animations to render from the active pack if available;
  // fall back to legacy mirror fields for Phase 2 backward compat.
  const renderGlb = activePack?.animationsGlbUrl ?? mesh?.animationsGlbUrl;
  const renderAnimations = activePack?.animations ?? mesh?.animations ?? [];

  // Phase 5 — if a skin is active AND ready, render its GLB instead of the
  // source mesh GLB on the static viewer. Animated viewer keeps using the
  // rigged/animated GLB (skins only re-texture the source mesh; the rig was
  // built before reskinning, so the animated GLB still has the pre-skin
  // texture baked in). To re-skin the animated path, the user would have to
  // re-trigger the animation pipeline against the new mesh.
  const activeSkin = mesh?.activeSkinId
    ? mesh.skins?.find(s => s.skinId === mesh.activeSkinId && s.status === 'ready')
    : undefined
  const staticGlb = activeSkin?.glbUrl ?? mesh?.glbUrl

  // 3D viewer mode selection:
  //   - 'animated' → multi-clip GLB with chip picker (AnimationPicker)
  //   - 'static'   → single GLB with idle rotation (ModelViewer)
  //   - 'flat'     → 2D PNG fallback (mesh in-flight, failed, or no mesh)
  const meshStatus = mesh?.status ?? 'none';
  const inFlightMesh = new Set([
    'queued','claimed','meshing','validating','uploading',
    'animating_queued','animating_rigging','animating_retargeting',
    'animating_merging','animating_uploading',
  ]).has(meshStatus);
  // Phase 7a — when a 2D video clip is active and ready, force 'video2d' mode
  // (takes priority over the 3D viewers — explicit user choice, made from
  // VideoClipsList / AvatarClipDetail). Hides the 3D paths until the user clears activeVideoClipId.
  const activeVideoClip = avatar.activeVideoClipId
    ? avatar.videoClips?.find(c => c.clipId === avatar.activeVideoClipId && c.status === 'ready')
    : undefined;

  // Phase 5 — when a skin is active, force `static` viewerMode even on an
  // animated avatar, so the user actually SEES their new skin. Otherwise the
  // animated GLB (which has the pre-skin texture baked in) keeps rendering.
  const viewerMode: 'animated' | 'static' | 'flat' | 'video2d' =
    activeVideoClip
      ? 'video2d'
      : activeSkin
      ? 'static'
      : renderGlb && meshStatus === 'animated_ready' ? 'animated'
      : mesh?.glbUrl && meshStatus === 'ready' ? 'static'
      : 'flat';

  // Phase 7c — does the active clip have alpha, and HOW?
  //   'dual'           : H.264 dual-stream matting (RGB + alpha-mask). Universal
  //                      — works on every browser via canvas compositing.
  //                      Default for new matting jobs.
  //   'webm'           : Legacy WebM VP9-alpha from older matting runs. Real
  //                      alpha, but only Chromium decodes it (gated by
  //                      supportsVp9Alpha; iOS falls through to chroma-key).
  //   'matting-pending': Phase 7g — the user asked for true alpha but the
  //                      veed/fal follow-up hasn't landed yet. Render a
  //                      loader so we don't flash the magenta chroma-key
  //                      version before the matted one arrives.
  //   'chroma-key'     : Synthesized at display time by ChromaKeyVideo (canvas).
  //                      Fallback when matting wasn't requested or failed.
  //   null             : Opaque clip, normal <video>.
  const clipAlphaMode: 'dual' | 'webm' | 'matting-pending' | 'chroma-key' | null = activeVideoClip
    ? (activeVideoClip.matteRgbUrl && activeVideoClip.matteAlphaUrl
        ? 'dual'
        : activeVideoClip.webmAlphaUrl && supportsVp9Alpha()
          ? 'webm'
          : (activeVideoClip.transparency === 'matting' && activeVideoClip.matteStatus === 'pending')
            ? 'matting-pending'
            : (activeVideoClip.transparency && activeVideoClip.transparency !== 'none')
              ? 'chroma-key'
              : null)
    : null;
  const hasClipAlpha = clipAlphaMode !== null && clipAlphaMode !== 'matting-pending';

  // Human-readable status line shown below the viewer so the user always knows
  // what they're looking at. Keeps the implicit-state UX confusion at zero.
  const providerLabel =
    mesh?.provider === 'meshy' ? 'Meshy'
    : mesh?.provider === 'rodin-fal' ? 'Rodin'
    : mesh?.provider === 'tripo' ? 'Tripo'
    : mesh?.provider === 'text-source' ? 'Meshy text-to-3d'
    : mesh?.provider === 'multi-image-source' ? 'Meshy multi-image'
    : mesh?.provider;
  const videoProviderLabel =
    activeVideoClip?.provider === 'hailuo-fal' ? 'Hailuo'
    : activeVideoClip?.provider === 'kling-fal' ? 'Kling 2.1'
    : activeVideoClip?.provider === 'pixverse-fal' ? 'PixVerse'
    : activeVideoClip?.provider === 'vidu-fal' ? 'Vidu Q3'
    : activeVideoClip?.provider === 'veo31-fal' ? 'Veo 3.1'
    : activeVideoClip?.provider === 'veo31-fast-fal' ? 'Veo 3.1 Fast'
    : activeVideoClip?.provider;
  // Le fasi della mesh, scritte per esteso (chiavi controllabili da verify-lingue).
  const meshPhaseLabels: Record<string, string> = {
    queued: t('detail.meshPhase.queued'),
    claimed: t('detail.meshPhase.claimed'),
    meshing: t('detail.meshPhase.meshing'),
    validating: t('detail.meshPhase.validating'),
    uploading: t('detail.meshPhase.uploading'),
    animating_queued: t('detail.meshPhase.queued'),
    animating_rigging: t('detail.meshPhase.rigging'),
    animating_retargeting: t('detail.meshPhase.retargeting'),
    animating_merging: t('detail.meshPhase.merging'),
    animating_uploading: t('detail.meshPhase.uploading'),
  };
  const statusLine = (() => {
    if (activeVideoClip) {
      const parts = [
        t('detail.status.video2d', { provider: videoProviderLabel }),
        activeVideoClip.resolution,
        activeVideoClip.durationSec ? `${activeVideoClip.durationSec}s` : null,
      ].filter(Boolean);
      return parts.join(' · ');
    }
    if (activeSkin) {
      return t('detail.status.skin', { provider: providerLabel, skin: activeSkin.label ?? t('detail.status.skinCustom') });
    }
    if (viewerMode === 'animated') {
      const lib = activePack?.library === 'meshy-dancing' ? 'Meshy Dancing'
        : activePack?.library === 'meshy-full' ? 'Meshy Full'
        : activePack?.library === 'tripo-utility' ? 'Tripo Action'
        : activePack?.library;
      return t('detail.status.animated', { provider: providerLabel, pack: lib });
    }
    if (viewerMode === 'static') return t('detail.status.static', { provider: providerLabel });
    if (viewerMode === 'flat') return t('detail.status.flat', { model: avatar.model });
    if (inFlightMesh) return t('detail.status.processing', { phase: meshPhaseLabels[meshStatus] ?? meshStatus });
    if (meshStatus === 'failed' || meshStatus === 'animated_failed') return t('detail.status.failed3d', { model: avatar.model });
    return null;
  })();
  const statusIcon = viewerMode === 'video2d' ? Film
    : viewerMode === 'flat' ? ImageIcon
    : Box;

  // Show the series nav strip whenever this avatar belongs to a multi-version
  // series (seriesCount preview from the avatar) — even before the full series
  // payload arrives, so the layout doesn't shift after the network round-trip.
  const showSeriesNav = seriesCount > 1;

  return (
    <div className="flex flex-col gap-4 px-4 pb-24 pt-4">
      {showSeriesNav && (
        <div className="flex items-center justify-between gap-2 rounded-xl border border-border bg-bg-secondary px-2 py-1.5">
          <button
            type="button"
            onClick={() => goSibling(prevAvatar)}
            disabled={!prevAvatar}
            aria-label={t('detail.newerVersion')}
            className={cn(
              'flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold',
              prevAvatar ? 'text-fg active:opacity-70' : 'text-fg-muted/40',
            )}
          >
            <ChevronLeft aria-hidden className="h-4 w-4" />
            {t('detail.newer')}
          </button>
          <Link
            to={`/avatars/${avatar._id}/series`}
            className="flex items-center gap-1 text-xs font-medium text-fg-muted active:opacity-70"
          >
            <Layers aria-hidden className="h-3.5 w-3.5" />
            {currentIndex >= 0
              ? `${currentIndex + 1} / ${total}`
              : t('detail.versions', { count: seriesCount })}
          </Link>
          <button
            type="button"
            onClick={() => goSibling(nextAvatar)}
            disabled={!nextAvatar}
            aria-label={t('detail.olderVersion')}
            className={cn(
              'flex items-center gap-1 rounded-lg px-2 py-1.5 text-xs font-semibold',
              nextAvatar ? 'text-fg active:opacity-70' : 'text-fg-muted/40',
            )}
          >
            {t('detail.older')}
            <ChevronRight aria-hidden className="h-4 w-4" />
          </button>
        </div>
      )}

      {viewerMode === 'video2d' && activeVideoClip?.videoUrl ? (
        <div className={cn('relative overflow-hidden rounded-2xl border border-black/40', aspectClass, hasClipAlpha ? 'bg-transparent' : 'bg-black')}>
          {clipAlphaMode === 'dual' ? (
            // H.264 RGB + alpha-mask pair → canvas composite. Universal.
            // Edge decomposite: legacy clips have chromaKeyHex (magenta
            // backdrop from preprocessing). New clips skip preprocessing
            // so veed pre-mattes the RGB stream to BLACK in the
            // background region — verified empirically (corners read
            // rgb(0,0,0) on freshly-minted dual-stream output). Default
            // to black so the decomposite always runs and edge pixels
            // get clean subject colors instead of dark fringing.
            <DualStreamVideo
              key={`dual-${activeVideoClip.clipId}`}
              rgbUrl={activeVideoClip.matteRgbUrl!}
              alphaUrl={activeVideoClip.matteAlphaUrl!}
              poster={activeVideoClip.posterUrl ?? avatar.imageUrl}
              knownBgHex={effectiveBgHexFor(activeVideoClip)}
            />
          ) : clipAlphaMode === 'webm' ? (
            // Legacy WebM VP9-alpha — Chromium-only. Plain <video>.
            <video
              key={`webm-${activeVideoClip.clipId}`}
              src={activeVideoClip.webmAlphaUrl!}
              poster={activeVideoClip.posterUrl ?? avatar.imageUrl}
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-contain"
            />
          ) : clipAlphaMode === 'matting-pending' ? (
            // True-alpha matting follow-up is still running on veed/fal —
            // show a loader instead of the raw magenta MP4 so the user
            // doesn't see the chroma-key version "flash" before the proper
            // matted dual-stream arrives.
            <div className="flex aspect-square w-full flex-col items-center justify-center gap-3 text-fg-muted">
              {avatar.imageUrl && (
                <img
                  src={avatar.imageUrl}
                  alt={avatar.title}
                  className="absolute inset-0 h-full w-full object-cover opacity-30"
                />
              )}
              <div className="relative flex flex-col items-center gap-2">
                <Loader2 aria-hidden className="h-8 w-8 animate-spin text-brand" />
                <p className="text-xs font-semibold">{t('clips.applyingMatte')}</p>
              </div>
            </div>
          ) : clipAlphaMode === 'chroma-key' ? (
            <ChromaKeyVideo
              key={activeVideoClip.clipId}
              src={activeVideoClip.videoUrl}
              poster={activeVideoClip.posterUrl ?? avatar.imageUrl}
              keyColor={activeVideoClip.chromaKeyHex ?? '#FF00FF'}
            />
          ) : (
            <video
              key={activeVideoClip.clipId}
              src={activeVideoClip.videoUrl}
              poster={activeVideoClip.posterUrl ?? avatar.imageUrl}
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
            />
          )}
        </div>
      ) : viewerMode === 'animated' ? (
        <AnimationPicker
          avatarId={avatar._id}
          packId={activePack?.packId}
          glbUrl={renderGlb!}
          poster={mesh?.animationsPosterUrl ?? avatar.thumbnailUrl}
          animations={renderAnimations}
        />
      ) : viewerMode === 'static' ? (
        <div className={cn('relative overflow-hidden rounded-2xl border border-black/40 bg-black', aspectClass)}>
          <Suspense
            fallback={
              <img
                src={mesh?.posterUrl ?? avatar.thumbnailUrl ?? avatar.imageUrl}
                alt={avatar.title}
                className="h-full w-full object-cover"
              />
            }
          >
            <ModelViewer
              src={staticGlb!}
              poster={mesh?.posterUrl ?? avatar.thumbnailUrl ?? avatar.imageUrl}
              alt={avatar.title}
              className="h-full w-full"
              style={{ backgroundColor: 'transparent' }}
              autoRotate={false}
              cameraControls={true}
              cameraOrbit="0deg 75deg auto"
            />
          </Suspense>
        </div>
      ) : (
        <div className={cn('relative overflow-hidden rounded-2xl border border-black/40 bg-black', aspectClass)}>
          {avatar.imageUrl && (
            <img
              src={avatar.imageUrl}
              alt={avatar.title}
              className="h-full w-full object-cover"
              loading="eager"
            />
          )}
          {inFlightMesh && (
            <div className="absolute inset-x-0 bottom-0 flex items-center justify-center gap-2 bg-black/60 px-3 py-2 text-xs text-white backdrop-blur">
              <Loader2 aria-hidden className="h-3.5 w-3.5 animate-spin" />
              {t('detail.building3d')}
            </div>
          )}
        </div>
      )}

      {statusLine && (
        <div className="flex items-center gap-2 -mt-2 text-[11px] text-fg-muted">
          {(() => { const StatusIcon = statusIcon; return <StatusIcon aria-hidden className="h-3 w-3" /> })()}
          <span>{statusLine}</span>
        </div>
      )}

      <PackSwitcher avatar={avatar} />

      <SkinSelector
        mesh={mesh}
        onPick={onPickSkin}
        onAdd={puoCreare ? () => setReskinModalOpen(true) : undefined}
      />

      <VideoClipsList
        avatar={avatar}
        onAdd={puoCreare ? () => setAnimate2dOpen(true) : undefined}
        onReroll={puoCreare ? onRerollVideoClip : undefined}
        onRetry={puoCreare ? onRetryVideoClip : undefined}
        onShowDetails={setDetailsClipId}
      />

      <h1 className="text-xl font-bold text-fg">{avatar.title}</h1>

      <button
        type="button"
        onClick={onSetFeatured}
        disabled={!!avatar.isFeatured || settingFeatured}
        className={cn(
          'flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-opacity active:opacity-80',
          avatar.isFeatured
            ? 'bg-brand/15 text-brand'
            : 'bg-bg-secondary text-fg',
          settingFeatured && 'opacity-60',
        )}
      >
        <Star
          aria-hidden
          className={cn('h-4 w-4', avatar.isFeatured && 'fill-current')}
        />
        {avatar.isFeatured ? t('detail.hubAvatar') : t('detail.setHubAvatar')}
      </button>

      <button
        type="button"
        onClick={onToggleVisibility}
        disabled={updatingVisibility}
        className={cn(
          'flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-opacity active:opacity-80',
          avatar.visibility === 'public'
            ? 'bg-brand/10 text-fg'
            : 'bg-bg-secondary text-fg',
          updatingVisibility && 'opacity-60',
        )}
      >
        {avatar.visibility === 'public' ? (
          <Globe aria-hidden className="h-4 w-4" />
        ) : (
          <Lock aria-hidden className="h-4 w-4" />
        )}
        {avatar.visibility === 'public' ? t('detail.public') : t('detail.private')}
      </button>

      {puoCreare && (
        <>
          <Make3dCta
            mesh={mesh}
            hasAlpha={avatar.hasAlpha ?? false}
            isPending={isMeshPending}
            onTrigger={onMake3d}
          />

          <AnimateMeCta
            mesh={mesh}
            isPending={animLife.isPending}
            onOpenModal={onOpenLibraryModal}
          />
        </>
      )}

      {seriesCount > 1 && (
        <Link
          to={`/avatars/${avatar._id}/series`}
          className={cn(
            'flex items-center justify-between rounded-xl border border-brand bg-brand/10 px-4 py-3',
            'text-sm transition-opacity active:opacity-80',
          )}
        >
          <span className="flex items-center gap-2 font-semibold text-fg">
            <Layers aria-hidden className="h-4 w-4 text-brand" />
            {t('detail.partOfSeries', { count: seriesCount })}
          </span>
          <span aria-hidden className="text-xl font-bold text-brand">
            ›
          </span>
        </Link>
      )}

      <Section label={t('detail.sectionPrompt')}>{avatar.prompt}</Section>
      {avatar.style && <Section label={t('detail.sectionStyle')}>{avatar.style}</Section>}
      <Section label={t('detail.sectionModel')}>{avatar.model}</Section>

      <AnimateLibraryModal
        open={libraryModalOpen}
        mesh={mesh}
        compatibleProviders={templateCompatibleProviders}
        onClose={() => setLibraryModalOpen(false)}
        onPick={onPickLibrary}
      />

      <ReskinModal
        open={reskinModalOpen}
        isPending={isReskinPending}
        onClose={() => setReskinModalOpen(false)}
        onGenerate={onSubmitReskin}
      />

      <Animate2dModal
        open={animate2dOpen}
        isPending={isAnimate2dPending}
        errorMessage={animate2dError}
        onClose={() => { setAnimate2dOpen(false); setAnimate2dError(null); }}
        onGenerate={onSubmitAnimate2d}
      />

      <VideoClipDetailsModal
        open={!!detailsClipId}
        clip={(avatar.videoClips ?? []).find(c => c.clipId === detailsClipId) ?? null}
        onClose={() => setDetailsClipId(null)}
      />

      {showAnimOverlay && (
        <AnimationOverlay
          avatarId={id}
          onComplete={() => setShowAnimOverlay(false)}
          onFailed={() => setShowAnimOverlay(false)}
        />
      )}
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="mb-1.5 text-xs font-bold uppercase tracking-wide text-fg-muted">
        {label}
      </h2>
      <p className="text-sm leading-relaxed text-fg">{children}</p>
    </section>
  );
}
